import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import Container from "../../components/Container";
import Content from "../../components/Content";
import { useAuthState } from "../../components/AuthProvider";
import Flex from "../../components/Flex";
import { requiresClient } from "../../components/AuthProvider";
import Card from "../../components/Card";
import DefaultBreadcrumbs from "../../components/DefaultBreadcrumbs";
import DataTable from "../../components/DataTable";
import Tooltip from "../../components/Tooltip";
import Message from "../../components/Message";
import Button from "../../components/Button";

import {
  fetchAggregateCountData,
  fetchRecordCountData,
  fetchIdentitySpecCountData,
  fetchSystemNotificationData,
  fetchAPICallCountsSummary,
  fetchReviewQueueCountData,
  fetchBulkOrgData,
  fetchClientSubscriptionsData
} from "../../utils/fetchData";

import { isReviewQueueSubscribed } from "../../utils/functionUtils";

import styles from "./Dashboard.module.scss";
import Spinner from "../../components/Loader/Spinner";

const Dashboard = () => {
  const { user } = useAuthState();

  const [aggregateCountData, setAggregateCountData] = useState({});
  const [recordCountData, setRecordCountData] = useState([]);
  const [apiCallCountData, setApiCallCountData] = useState([]);
  const [identitySpecCountData, setIdentitySpecCountData] = useState({});
  const [reviewQueueCount, setReviewQueueCount] = React.useState(null);
  const [systemNotificationData, setSystemNotificationData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rqConfig, setRqConfg] = useState([]);
  const [isRecordCombinerPresent, setIsRecordCombinerPresent] = useState(false);
  const environments = ["local", "dev", "stage", "nonprod", "prod"];

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsLoading(true);
    const selectedClientId = user.preferences.selectedClient.id;
    const selectedClientOId = user.preferences.selectedClient.oid;
    const currentDate = new Date().toISOString().split("T")[0];

    Promise.all([
      fetchAggregateCountData(selectedClientId),
      fetchRecordCountData(selectedClientId),
      fetchIdentitySpecCountData(selectedClientId),
      fetchSystemNotificationData(currentDate),
      fetchAPICallCountsSummary(selectedClientOId),
      fetchReviewQueueCountData(user.preferences.selectedClient.oid),
      fetchBulkOrgData(user.preferences.selectedClient.oid),
      fetchClientSubscriptionsData(user.preferences.selectedClient.oid)
    ]).then(responses => {
      setIsLoading(false);
      if (responses[0].results) {
        setAggregateCountData(responses[0].results);
      }
      if (responses[1].results) {
        setRecordCountData(responses[1].results);
      }
      if (responses[2].results) {
        setIdentitySpecCountData(responses[2].results);
      }
      if (responses[3].results) {
        setSystemNotificationData(responses[3].results);
      }
      if (responses[4]) {
        setApiCallCountData(responses[4]);
      }
      if (responses[5].results) {
        setReviewQueueCount(responses[5].results.dynamicReviewQueue);
      }
      if (responses[6].length > 0) {
        setRqConfg(
          responses[6][0].clients[0].client_subscriptions[0].reviewQueues
        );
      }
      if (responses[7].results[0].record_combiner_client_sub_relations) {
        setIsRecordCombinerPresent(true);
      } else setIsRecordCombinerPresent(false);
    });
  }, [user.preferences.selectedClient.oid, user.preferences.selectedClient.id]);

  const columns = [
    {
      Header: "Source OID",
      accessor: "source_oid",
      canSort: true
    },
    {
      Header: "Source Name",
      accessor: "source_name",
      canSort: true
    },
    {
      Header: "Source Code",
      accessor: "source_code",
      canSort: true
    },
    {
      Header: "Added",
      accessor: "total_added",
      canSort: true
    },
    {
      Header: "Removed",
      accessor: "total_removed",
      canSort: true
    },
    {
      Header: "Updated",
      accessor: "total_updated",
      canSort: true
    }
  ];
  const apiCallsColumns = [
    {
      Header: "Name",
      accessor: "name",
      canSort: true
    },
    {
      Header: "Successful",
      accessor: "successful",
      canSort: true
    },
    {
      Header: "Client Side Error",
      accessor: "client_side_error",
      canSort: true
    },
    {
      Header: "Time Out",
      accessor: "timeouts",
      canSort: true
    },
    {
      Header: "Server Errors",
      accessor: "server_error",
      canSort: true
    }
  ];

  var aggTotal_added = aggregateCountData.total_added
    ? aggregateCountData.total_added
    : `0`;
  var aggTotal_removed = aggregateCountData.total_removed
    ? aggregateCountData.total_removed
    : `0`;
  var aggUpdated = aggregateCountData.total_updated
    ? aggregateCountData.total_updated
    : `0`;
  var sumSuccessful = 0,
    sumApiClientSideError = 0,
    sumApiServerErrors = 0;

  apiCallCountData.forEach(row => {
    sumSuccessful += row.successful;
    sumApiClientSideError += row.client_side_error;
    sumApiServerErrors += row.server_error;
  });
  var active = aggregateCountData.total_added
    ? aggregateCountData.total_added -
      (aggregateCountData.total_removed ? aggregateCountData.total_removed : 0)
    : `0`;
  var csv_report = [
    "Added, " + aggTotal_added,
    "Removed, " + aggTotal_removed,
    "Updated, " + aggUpdated,
    "Successful, " + sumSuccessful,
    "Client Side Error, " + sumApiClientSideError,
    "Server Errors, " + sumApiServerErrors,
    "Active, " + active
  ];
  function Identities_csv(csv) {
    var csv_string = csv.join("\n");
    var filename = "client_info" + new Date().toLocaleDateString() + ".csv";
    var link = document.createElement("a");
    link.style.display = "none";
    link.setAttribute("target", "_blank");
    link.setAttribute(
      "href",
      "data:text/csv;charset=utf-8," + encodeURIComponent(csv_string)
    );
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  return (
    <Layout>
      <div className={styles.base}>
        <Container>
          <Flex gutter={3}>
            <Flex.Item span={12}>
              <DefaultBreadcrumbs />
            </Flex.Item>
            {isLoading ? (
              <Spinner />
            ) : (
              <>
                <Flex.Item span={12}>
                  <Content>
                    <h1>Dashboard</h1>
                  </Content>
                </Flex.Item>
                {systemNotificationData.map(notification => (
                  <Flex.Item span={12} key={notification.id}>
                    <Content>
                      <Message
                        type="warning"
                        onDismiss={() => {
                          const nextNotifications = systemNotificationData.filter(
                            currentNotification =>
                              currentNotification !== notification
                          );
                          setSystemNotificationData(nextNotifications);
                        }}
                      >
                        {notification.message}
                      </Message>
                    </Content>
                  </Flex.Item>
                ))}
                <Flex.Item span={8}>
                  <Card className={styles.card}>
                    <h2>
                      Welcome, {user.firstName} {user.lastName}
                    </h2>
                    <div className={styles.links}>
                      <Flex gutter={3}>
                        <Flex.Item span={6}>
                          <ul>
                            <li>
                              <a href="/search-identities-new">
                                Search Identities
                              </a>
                            </li>
                            {user.role.includes("Global Admin") && (
                              <li>
                                <a href="/data-quality">Data Quality</a>
                              </li>
                            )}
                          </ul>
                        </Flex.Item>

                        <Flex.Item span={6}>
                          {user.role.includes("Admin") && (
                            <ul>
                              <li>
                                <a href="/admin/organizations">
                                  Manage Organizations
                                </a>
                              </li>
                              <li>
                                <a href="/admin/users">Manage Users</a>
                              </li>
                              {user.role.includes("Global Admin") && (
                                <li>
                                  <a href="/tasks">Tasks</a>
                                </li>
                              )}
                            </ul>
                          )}
                        </Flex.Item>
                      </Flex>
                    </div>
                  </Card>
                  <Card className={styles.card}>
                    <h2 className={styles.title}>
                      Records by Source{" "}
                      <Tooltip>
                        <Content maxWidth="15rem">
                          <p>
                            The number of patient records added, removed, and
                            updated per source in this client.
                          </p>
                        </Content>
                      </Tooltip>
                    </h2>
                    <DataTable
                      columns={columns}
                      data={recordCountData}
                      hasPagination
                      hasDownloadCsv={true}
                      downloadCsvName="source_info.csv"
                      // maxWidth="43rem"
                      hasSorting
                    />
                  </Card>
                  {user.role === "Global Admin" && (
                    <Card className={styles.card}>
                      <h2 className={styles.title}>
                        API Call Counts{" "}
                        <Tooltip>
                          <Content maxWidth="15rem">
                            <p>
                              The number of API Calls for this client month to
                              date.
                            </p>
                          </Content>
                        </Tooltip>
                      </h2>
                      <DataTable
                        columns={apiCallsColumns}
                        data={apiCallCountData}
                        hasPagination
                        // maxWidth="43rem"
                        hasSorting
                      />
                    </Card>
                  )}
                </Flex.Item>
                <Flex.Item span={4}>
                  <Card className={styles.card}>
                    <h4>
                      Identities{" "}
                      <Tooltip>
                        <Content maxWidth="30rem">
                          <p>
                            <b>Added</b> - The total number of lives added to
                            this client.
                          </p>
                          <p>
                            <b>Removed</b> - The total number of lives removed
                            from this client due to API requests or Manual
                            stewardship.
                          </p>
                          <p>
                            <b>Updated</b> - The total number of lives updated
                            in this client due to API requests or Manual
                            stewardship.
                          </p>
                          <p>
                            <b>Successful</b> - Successful API Calls for this
                            client month to date.
                          </p>
                          <p>
                            <b>Client Side Error</b> - Client Side Error month
                            to date.
                          </p>
                          <p>
                            <b>Server Errors</b> - Server Errors for this client
                            month to date.
                          </p>
                          <p>
                            <b>Active</b> - The total number of active lives in
                            this client calculated by subtracting the total
                            number of lives removed from the total number of
                            lives added.
                          </p>
                        </Content>
                      </Tooltip>
                    </h4>
                    <div className={styles.counts}>
                      <table>
                        <tbody>
                          <tr>
                            <td>Added</td>
                            <td className={styles.alignRight}>
                              {aggTotal_added}
                            </td>
                          </tr>
                          <tr>
                            <td>Removed</td>
                            <td className={styles.alignRight}>
                              {aggTotal_removed}
                            </td>
                          </tr>
                          <tr>
                            <td>Updated</td>
                            <td className={styles.alignRight}>{aggUpdated}</td>
                          </tr>
                          <tr>
                            <td>Successful API Calls</td>
                            <td className={styles.alignRight}>
                              {sumSuccessful}
                            </td>
                          </tr>
                          <tr>
                            <td>Client Side Error</td>
                            <td className={styles.alignRight}>
                              {sumApiClientSideError}
                            </td>
                          </tr>
                          <tr>
                            <td>Server Errors</td>
                            <td className={styles.alignRight}>
                              {sumApiServerErrors}
                            </td>
                          </tr>
                          <tr>
                            <td>Active</td>
                            <td align="right">{active}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <Button
                      className={styles.button}
                      primary
                      onClick={() => Identities_csv(csv_report)}
                    >
                      Download CSV
                    </Button>
                  </Card>
                  <Card className={styles.card}>
                    <h4>
                      Manual Updates{" "}
                      <Tooltip>
                        <Content maxWidth="15rem">
                          <p>
                            <b>Added</b> - The total number of lives in this
                            client changed by Manual stewardship
                          </p>
                        </Content>
                      </Tooltip>
                    </h4>
                    <div className={styles.counts}>
                      <table>
                        <tbody>
                          <tr>
                            <td>Added</td>
                            <td>
                              {identitySpecCountData.total_added
                                ? identitySpecCountData.total_added.toLocaleString()
                                : `0`}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </Card>
                  {environments.includes(process.env.REACT_APP_ENV) &&
                    (isReviewQueueSubscribed(
                      rqConfig,
                      "IMDMNearMatchReviewQueue"
                    ) ||
                      isReviewQueueSubscribed(
                        rqConfig,
                        "NearMissReviewQueue"
                      ) ||
                      isReviewQueueSubscribed(rqConfig, "TwinsReviewQueue") ||
                      isReviewQueueSubscribed(
                        rqConfig,
                        "CollisionDetectionReviewQueue"
                      ) ||
                      isRecordCombinerPresent) && (
                      <Card className={styles.card}>
                        <h4>
                          Review Queue{" "}
                          <Tooltip>
                            <Content maxWidth="15rem">
                              <p>
                                <b>Close Matches</b> - Records were included in
                                an identity, but just barely
                              </p>
                              <p>
                                <b>Near Misses</b> - Records were excluded from
                                an identity, but just barely
                              </p>
                              <p>
                                <b>Twins</b> - Records possibly linked as they
                                belong to twins having same DOB & Address
                              </p>
                              <p>
                                <b>Overlays</b> - Records of a source which have
                                same recordId but different demographic data
                              </p>
                            </Content>
                          </Tooltip>
                        </h4>
                        <div className={styles.counts}>
                          <table>
                            <tbody>
                              {isReviewQueueSubscribed(
                                rqConfig,
                                "IMDMNearMatchReviewQueue"
                              ) && (
                                <tr>
                                  <td>Possible Over Merge</td>
                                  <td>
                                    {reviewQueueCount.IMDMNearMatchReviewQueue.toLocaleString()}
                                  </td>
                                </tr>
                              )}
                              {isReviewQueueSubscribed(
                                rqConfig,
                                "NearMissReviewQueue"
                              ) && (
                                <tr>
                                  <td>Near Misses</td>
                                  <td>
                                    {reviewQueueCount.NearMissReviewQueue.toLocaleString()}
                                  </td>
                                </tr>
                              )}
                              {isReviewQueueSubscribed(
                                rqConfig,
                                "TwinsReviewQueue"
                              ) && (
                                <tr>
                                  <td>Twins</td>
                                  <td>
                                    {reviewQueueCount.TwinsReviewQueue.toLocaleString()}
                                  </td>
                                </tr>
                              )}
                              {isReviewQueueSubscribed(
                                rqConfig,
                                "CollisionDetectionReviewQueue"
                              ) && (
                                <tr>
                                  <td>Collision Review Queue</td>
                                  <td>
                                    {reviewQueueCount.CollisionDetectionReviewQueue.toLocaleString()}
                                  </td>
                                </tr>
                              )}
                              {isRecordCombinerPresent && (
                                <tr>
                                  <td>Overlays</td>
                                  <td>
                                    {reviewQueueCount.OverlayReviewQueue.toLocaleString()}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </Card>
                    )}
                </Flex.Item>
              </>
            )}
          </Flex>
        </Container>
      </div>
    </Layout>
  );
};

export default requiresClient(Dashboard);
